import { useCallback, useEffect, useRef, useState } from "react";
import { EMPTY_ZIP_SIZE, getZipWithoutDownload } from "@utils/utilsApi";

/**
 * Per ogni id verifica se lo zip degli allegati contiene almeno un file.
 * Le API di lista non espongono questa informazione, quindi viene scaricato
 * lo zip di ogni riga. I risultati restano in cache finché non si chiama invalidate.
 */
export const useAttachmentsAvailability = (
  ids: number[],
  getUrl: (id: number) => string,
) => {
  const [availability, setAvailability] = useState<Record<number, boolean>>({});
  const [generation, setGeneration] = useState(0);
  const requestedIds = useRef<Set<number>>(new Set());
  const getUrlRef = useRef(getUrl);
  getUrlRef.current = getUrl;

  const idsKey = ids.join(",");

  useEffect(() => {
    let active = true;
    const requested = requestedIds.current;
    const idsToCheck = idsKey
      .split(",")
      .filter(Boolean)
      .map(Number)
      .filter((id) => !requested.has(id));
    const pending = new Set(idsToCheck);

    idsToCheck.forEach((id) => {
      requested.add(id);
      getZipWithoutDownload(getUrlRef.current(id)).then((blob) => {
        if (!active) return;
        pending.delete(id);
        setAvailability((prev) => ({
          ...prev,
          [id]: !!blob && blob.size > EMPTY_ZIP_SIZE,
        }));
      });
    });

    return () => {
      active = false;
      // Le richieste non concluse vanno ripetute al prossimo giro
      pending.forEach((id) => requested.delete(id));
    };
  }, [idsKey, generation]);

  const invalidate = useCallback(() => {
    requestedIds.current.clear();
    setAvailability({});
    setGeneration((prev) => prev + 1);
  }, []);

  // true finché almeno una riga non ha ancora ricevuto l'esito della verifica
  const loading = ids
    .filter(Boolean)
    .some((id) => availability[id] === undefined);

  return { availability, invalidate, loading };
};
